import { type ReactNode, useEffect, useRef, useState } from 'react'
import { api, type PickedTitle } from '../api'
import { CatalogDialog } from '../catalog/Dialog'
import { Poster } from '../catalog/Search'
import { useFeatures } from '../features'
import { formatRate, formatSize, useI18n } from '../i18n'
import type { CatalogRef, FileEntry, Source } from '../protocol'
import { Icon } from '../ui/Icon'
import type { RoomClient, RoomState } from './client'

/**
 * The room's tab next to the chat, in three parts: what the room watches
 * (the catalog title), the release (the torrent) and its files. Each part
 * has its own actions in its heading.
 */
export function SourcePanel({ client, state }: { client: RoomClient; state: RoomState }) {
  const { t } = useI18n()
  const features = useFeatures()
  const isOwner = state.you?.role === 'owner'
  const catalogOn = isOwner && !!features?.catalog
  const { source, title } = state
  const roomId = state.room!.id
  // Picking another release while one plays.
  const [changing, setChanging] = useState(false)
  // The catalog over the room, optionally opened on a title.
  const [catalog, setCatalog] = useState<{ at?: PickedTitle } | null>(null)
  const [error, setError] = useState('')
  // A torrent added from anywhere closes the picker.
  const infoHash = source?.infoHash
  useEffect(() => {
    if (infoHash) setChanging(false)
  }, [infoHash])

  const titleAt = title ? { type: title.type, id: title.id, season: title.season } : undefined
  // The release comes after the title. Without the catalog no title can be
  // chosen, and a release already there (an older room) stays in view.
  const catalogOff = !features?.catalog
  const showTitle = !!title || catalogOn
  const showRelease = !!source || !!title || (isOwner && catalogOff)
  const videos = sortNatural((source?.files ?? []).filter((f) => f.kind === 'video'))
  const picking = isOwner && (!source || changing)

  return (
    <div className="source">
      {catalog && <CatalogDialog roomId={roomId} initial={catalog.at} onClose={() => setCatalog(null)} />}

      {showTitle && (
        <Section
          label={t('source.sectionTitle')}
          actions={
            catalogOn &&
            title && (
              <>
                <button type="button" className="text-btn" onClick={() => setCatalog({})}>
                  {t('source.change')}
                </button>
                <button
                  type="button"
                  className="icon-btn source__clear"
                  aria-label={t('title.clear')}
                  title={t('title.clear')}
                  onClick={() => {
                    setError('')
                    api.clearTitle(roomId).catch((e) => setError(e instanceof Error ? e.message : String(e)))
                  }}
                >
                  <Icon name="close" />
                </button>
              </>
            )
          }
        >
          {title ? (
            <CatalogSummary entry={title} />
          ) : (
            <div className="source__empty-title">
              <button type="button" className="btn btn--primary btn--wide" onClick={() => setCatalog({})}>
                <Icon name="film" /> {t('title.choose')}
              </button>
              <p className="muted small">{t('title.hint')}</p>
            </div>
          )}
          {error && <p className="error-text">{error}</p>}
        </Section>
      )}

      {!showTitle && !showRelease && <p className="muted">{t('source.nothingYet')}</p>}

      {showRelease && (
        <Section
          label={t('source.sectionRelease')}
          actions={
            isOwner &&
            source && (
              <button type="button" className="text-btn" onClick={() => setChanging(!changing)}>
                {changing ? t('source.cancel') : t('source.change')}
              </button>
            )
          }
        >
          {picking ? (
            <div className="source__picker">
              {catalogOn && (
                <>
                  <button type="button" className="btn btn--primary btn--wide" onClick={() => setCatalog({ at: titleAt })}>
                    <Icon name="film" /> {title ? t('source.findReleases') : t('catalog.open')}
                  </button>
                  <p className="source__or">{t('source.orOwn')}</p>
                </>
              )}
              <AddSource roomId={roomId} onDone={() => setChanging(false)} />
            </div>
          ) : source ? (
            <ReleaseCard
              source={source}
              title={title}
              onFind={catalogOn && titleAt ? () => setCatalog({ at: titleAt }) : undefined}
            />
          ) : (
            <p className="muted">{t('source.noTorrent')}</p>
          )}
        </Section>
      )}

      {source?.status === 'ready' && !picking && (videos.length !== 1 || state.media?.fileIndex !== videos[0].index) && (
        <Section label={videos.length > 1 ? t('source.sectionEpisodes', { n: videos.length }) : t('source.sectionFile')}>
          <Episodes client={client} state={state} videos={videos} />
        </Section>
      )}
    </div>
  )
}

function Section({ label, actions, children }: { label: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="source__section">
      <header className="source__section-head">
        <h3>{label}</h3>
        {actions && <div className="source__section-actions">{actions}</div>}
      </header>
      {children}
    </section>
  )
}

/** What the room watches according to the catalog. */
function CatalogSummary({ entry }: { entry: CatalogRef }) {
  const { t, lang } = useI18n()
  const title = entry.titles[lang] || entry.titles.original || entry.titles.en
  const facts = [entry.year, entry.season ? t('catalog.season', { n: entry.season }) : null].filter(Boolean).join(', ')
  return (
    <div className="source__catalog-entry">
      <Poster path={entry.poster} size="w92" className="source__poster" />
      <div>
        <p className="source__title">{title}</p>
        {facts && <p className="muted">{facts}</p>}
      </div>
    </div>
  )
}

/**
 * The room's torrent and how it downloads. A torrent added for another
 * title than the room's says so: the title was changed since.
 */
function ReleaseCard({ source, title, onFind }: { source: Source; title: CatalogRef | null; onFind?: () => void }) {
  const { t, lang } = useI18n()
  const other = !!title && !!source.forTitle && source.forTitle !== `${title.type}:${title.id}`
  const name = title ? title.titles[lang] || title.titles.original || title.titles.en : ''
  return (
    <div className={`source__release${other ? ' is-other' : ''}`}>
      {other && (
        <div className="source__other">
          <p>{t('source.otherTitle')}</p>
          {onFind && (
            <button type="button" className="btn btn--small btn--primary" onClick={onFind}>
              {t('source.findForTitle', { title: name })}
            </button>
          )}
        </div>
      )}
      <p className="source__name">{source.name || t('source.torrent')}</p>
      {source.status === 'metadata' && <p className="source__status muted">{t('source.metadata')}</p>}
      {source.status === 'error' && <p className="error-text">{source.error}</p>}
      {source.stats && (
        <p className="source__status muted">
          <span className={`source__dot${source.stats.peers > 0 ? ' is-on' : ''}`} aria-hidden="true" />
          {t('source.stats', { n: source.stats.peers, rate: formatRate(source.stats.downloadRate) })}
        </p>
      )}
    </div>
  )
}

function Episodes({ client, state, videos }: { client: RoomClient; state: RoomState; videos: FileEntry[] }) {
  const { t } = useI18n()
  const isOwner = state.you?.role === 'owner'
  const source = state.source!
  const current = state.media?.fileIndex
  const duration = state.media?.info?.duration
  const names = shortNames(videos.map((f) => fileName(f.path)))
  if (videos.length === 0) return <p className="muted">{t('source.noVideos')}</p>
  return (
    <>
      {isOwner && videos.length > 1 && (
        <label className="toggle">
          <input
            type="checkbox"
            checked={state.autoplayNext}
            onChange={(e) => client.send({ t: 'set_autoplay', on: e.target.checked })}
          />
          <span>{t('source.autoplay')}</span>
        </label>
      )}
      <ol className="episodes">
        {videos.map((f, i) => {
          const progress = source.progress?.[f.index] ?? 0
          const total = f.index === current && duration ? duration : 0
          const active = f.index === current
          const body = (
            <>
              <span className="episodes__name" title={fileName(f.path)}>
                {/^\d+$/.test(names[i]) ? t('source.episode', { n: Number(names[i]) }) : names[i]}
              </span>
              <span className="episodes__meta muted">{formatSize(f.length)}</span>
              {progress > 0 && total > 0 && (
                <span className="episodes__progress" style={{ width: `${Math.min(100, (progress / total) * 100)}%` }} />
              )}
            </>
          )
          return (
            <li key={f.index} className={active ? 'is-active' : ''} aria-current={active ? 'true' : undefined}>
              {isOwner ? (
                <button type="button" onClick={() => client.send({ t: 'select_media', fileIndex: f.index })}>
                  {body}
                </button>
              ) : (
                <div>{body}</div>
              )}
            </li>
          )
        })}
      </ol>
    </>
  )
}

/** A torrent of one's own: a magnet link or a .torrent file. */
function AddSource({ roomId, onDone }: { roomId: string; onDone: () => void }) {
  const { t } = useI18n()
  const [magnet, setMagnet] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      className="add-source"
      onSubmit={(e) => {
        e.preventDefault()
        if (magnet.trim()) void run(() => api.addMagnet(roomId, magnet.trim()))
      }}
    >
      <div className="add-source__row">
        <input
          value={magnet}
          onChange={(e) => setMagnet(e.target.value)}
          placeholder="magnet:?xt=urn:btih:…"
          aria-label={t('source.magnet')}
          spellCheck={false}
          autoComplete="off"
        />
        <button type="submit" className="btn btn--primary" disabled={busy || !magnet.trim().startsWith('magnet:?')}>
          {t('source.add')}
        </button>
      </div>
      <div className="add-source__actions">
        <button type="button" className="btn btn--wide" disabled={busy} onClick={() => fileRef.current?.click()}>
          <Icon name="upload" /> {t('source.upload')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".torrent,application/x-bittorrent"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void run(() => api.addTorrentFile(roomId, f))
            e.target.value = ''
          }}
        />
      </div>
      {error && <p className="error-text">{error}</p>}
    </form>
  )
}

function fileName(p: string) {
  return p.split('/').pop() ?? p
}

const SEPARATOR = /[\s._\-–—[\]()]/

/**
 * Episode files share most of their names ("Show - 01 (1080p).mkv"): the
 * common start and end, cut at separators, are dropped. Names that would
 * become empty stay whole.
 */
export function shortNames(names: string[]): string[] {
  const bare = names.map((n) => n.replace(/\.[a-z0-9]{2,4}$/i, ''))
  if (bare.length < 2) return bare
  let prefix = bare.reduce((p, n) => {
    let i = 0
    while (i < p.length && i < n.length && p[i] === n[i]) i++
    return p.slice(0, i)
  })
  let suffix = bare.reduce((s, n) => {
    let i = 0
    while (i < s.length && i < n.length && s[s.length - 1 - i] === n[n.length - 1 - i]) i++
    return s.slice(s.length - i)
  })
  // Back to a separator, so that "Show - 0" does not eat the 0 of "01".
  while (prefix && !SEPARATOR.test(prefix[prefix.length - 1])) prefix = prefix.slice(0, -1)
  while (suffix && !SEPARATOR.test(suffix[0])) suffix = suffix.slice(1)
  const short = bare.map((n) =>
    n
      .slice(prefix.length, n.length - suffix.length)
      .replace(/^[\s._\-–—]+|[\s._\-–—]+$/g, ''),
  )
  return short.some((s) => !s) || prefix.length + suffix.length === 0 ? bare : short
}

function sortNatural(files: FileEntry[]) {
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
  return [...files].sort((a, b) => collator.compare(a.path, b.path))
}
